import axios from 'axios';
import { config } from './config';
import { tokens } from './tokens';
import { err, ok } from 'neverthrow';
export const auth = (() => {
    const _axios = axios.create(); //private instance without interceptors!
    let interceptorInstancesMap = new Map();
    let { token, refreshToken } = tokens.get();
    let _retryCounter = 0;
    function setConfig(toApply) {
        config.set(toApply);
    }
    async function login(email, password) {
        if (!email || !password) {
            const errArguments = [];
            if (!email)
                errArguments.push('email');
            if (!password)
                errArguments.push('password');
            return err([
                {
                    key: 'credentials_required',
                    message: 'Please provide all de required fields.',
                    arguments: errArguments,
                },
            ]);
        }
        try {
            const response = await _axios.post(`${config.fullUrl()}/tokens`, {
                email: email,
                password: password,
            });
            if (response && response.status === 200) {
                const storedTokens = tokens.set(response.headers);
                token = storedTokens.token;
                refreshToken = storedTokens.refreshToken;
                applyInterceptors();
                return ok(storedTokens);
            }
            return err(response.data);
        }
        catch (error) {
            if (error &&
                error.response &&
                error.response.data &&
                Array.isArray(error.response.data)) {
                return err(error.response.data);
            }
            return err([
                {
                    key: 'credentials_required',
                    message: 'no tokens',
                    arguments: ['refresh-token'],
                },
            ]);
        }
    }
    function logout() {
        token = refreshToken = '';
        tokens.set();
    }
    async function refresh() {
        if (!refreshToken) {
            return err([
                {
                    key: 'credentials_required',
                    message: 'no tokens',
                    arguments: ['refresh-token'],
                },
            ]);
        }
        try {
            const response = await _axios.get(`${config.fullUrl()}/tokens`, {
                headers: {
                    Authorization: `Bearer ${refreshToken}`,
                },
            });
            if (response && response.status === 200) {
                const storedTokens = tokens.set(response.headers);
                token = storedTokens.token;
                refreshToken = storedTokens.refreshToken;
                return ok(storedTokens);
            }
            return err(response.data);
        }
        catch (error) {
            _retryCounter = 0;
            if (error && error.response && error.response.data) {
                return err(error.response.data);
            }
            return err([
                {
                    key: 'credentials_required',
                    message: 'no tokens',
                    arguments: ['refresh-token'],
                },
            ]);
        }
    }
    function applyInterceptors(instance) {
        if (!interceptorInstancesMap.get(instance)) {
            instance = instance || axios; //normalize fallback to default instance.
            let ref = instance.interceptors.request.use(async (config) => {
                return await tokenInterceptor(config);
            });
            // Response interceptor
            instance.interceptors.response.use((value) => value, responseErrorHandler);
            interceptorInstancesMap.set(instance, ref);
        }
    }
    function ejectInterceptors(instance) {
        instance.interceptors.request.eject(interceptorInstancesMap.get(instance));
        interceptorInstancesMap.delete(instance);
    }
    async function tokenInterceptor(requestConfig) {
        const { token } = tokens.get();
        if (token) {
            return {
                ...requestConfig,
                headers: {
                    ...requestConfig.headers,
                    Authorization: `Bearer ${token}`,
                },
            };
        }
        return { ...requestConfig, headers: { ...requestConfig.headers } };
    }
    async function responseErrorHandler(error) {
        if (error &&
            error.response &&
            'status' in error.response &&
            error.response.status === 401 &&
            _retryCounter < 10) {
            try {
                _retryCounter++;
                await refresh();
                const retryRequest = {
                    ...error.config,
                    headers: {
                        ...error.config.headers,
                        Authorization: `Bearer ${token}`,
                    },
                };
                return axios(retryRequest).then((response) => {
                    if (response && response.status > 199 && response.status < 300) {
                        _retryCounter = 0;
                    }
                    return response;
                });
            }
            catch (error) {
                _retryCounter = 0;
                throw error.response.data;
            }
        }
        _retryCounter = 0;
        return Promise.reject(error);
    }
    const api = {
        config: setConfig,
        login: login,
        logout: logout,
        refresh: refresh,
        applyInterceptors: applyInterceptors,
        ejectInterceptors: ejectInterceptors,
        isAuthenticated: (async () => {
            return err([
                {
                    key: 'getter_not_defined',
                    message: 'Getter is not defined yet, implementation is below. Otherwise the function is invoked immediately!',
                    arguments: [],
                },
            ]);
        })(),
    };
    Object.defineProperty(api, 'isAuthenticated', {
        get() {
            return (async () => {
                const { token, refreshToken } = tokens.get();
                if (token) {
                    applyInterceptors();
                    return ok({ token: token, refreshToken: refreshToken });
                }
                if (refreshToken) {
                    const response = await refresh();
                    if (response.isOk())
                        applyInterceptors();
                    return response;
                }
                return err([
                    {
                        key: 'credentials_required',
                        message: 'Unauthorized',
                        arguments: ['unauthorized'],
                    },
                ]);
            })();
        },
    });
    return api;
})();
